import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const ClinicRewardsView = ({ onClose }) => {
  const [clinicData, setClinicData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [redemptionType, setRedemptionType] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [cashAmount, setCashAmount] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [pendingRedemptions, setPendingRedemptions] = useState([]);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [productOrders, setProductOrders] = useState([]);
  const [fulfillingOrder, setFulfillingOrder] = useState(null);
  const [materialRequests, setMaterialRequests] = useState([]);
  const [completingMaterial, setCompletingMaterial] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [auditClinic, setAuditClinic] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const loadProductOrders = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/product-orders`);
      if (res.ok) { const d = await res.json(); setProductOrders(d.orders || []); }
    } catch (e) { console.error("Rewards error:", e); }
  };

  // Product catalog for clinic redemption (must match backend PRODUCT_CATALOGUE exactly)
  const [productCatalog, setProductCatalog] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, pendingRes, catalogRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/pending-redemptions`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/vet-products`)
        ]);
        if (statsRes.ok) setClinicData(await statsRes.json());
        else setError('Failed to fetch clinic rewards data');
        if (pendingRes.ok) {
          const pd = await pendingRes.json();
          setPendingRedemptions(pd.redemptions || []);
        }
        if (catalogRes.ok) {
          const cd = await catalogRes.json();
          const catalogObj = {};
          for (const p of (cd.products || [])) {
            catalogObj[p.product] = {
              variations: p.variations.map(v => ({ name: v.name, price: v.points }))
            };
          }
          setProductCatalog(catalogObj);
        }
        await loadProductOrders();
        // Load material requests
        try {
          const matRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vet-portal/material-requests`);
          if (matRes.ok) { const md = await matRes.json(); setMaterialRequests(md.requests || []); }
        } catch (e) { console.error("Rewards error:", e); }
      } catch (err) {
        setError(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
  };

  const markAsPaid = async (redemptionId, clinicName, cashValue) => {
    setMarkingPaid(redemptionId);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/redemptions/${redemptionId}/mark-paid`, {
        method: 'POST'
      });
      if (res.ok) {
        showToast(`✅ ${clinicName} — $${cashValue} marked as paid`, 'success');
        setPendingRedemptions(prev => prev.filter(r => r.id !== redemptionId));
      } else {
        const err = await res.json();
        showToast(`❌ ${err.detail || 'Failed to mark as paid'}`, 'error');
      }
    } catch {
      showToast('❌ Failed to connect', 'error');
    } finally {
      setMarkingPaid(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] flex flex-col">
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="min-w-0 flex-1 mr-3">
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 truncate">Clinic Rewards Program</h2>
            <p className="text-xs md:text-base text-gray-600 hidden md:block">Track points, manage redemptions, and reward clinic partnerships</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 text-xl font-bold"
            data-testid="rewards-modal-close"
          >
            &times;
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading clinic rewards...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {clinicData && (
          <div className="space-y-6">

            {/* ── PENDING PRODUCT ORDERS ── */}
            {productOrders.filter(o => o.status === 'pending').length > 0 && (
              <div className="bg-white rounded-xl border-2 border-blue-300 overflow-hidden">
                <div className="bg-blue-50 px-5 py-3 border-b border-blue-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                    <h3 className="font-semibold text-blue-900">📦 Pending Product Orders — Needs Fulfilment</h3>
                  </div>
                  <span className="text-xs font-bold bg-blue-500 text-white px-2 py-0.5 rounded-full">
                    {productOrders.filter(o => o.status === 'pending').length}
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {productOrders.filter(o => o.status === 'pending').map(order => {
                    const date = order.ordered_at
                      ? new Date(order.ordered_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—';
                    return (
                      <div key={order.id} className="px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-bold text-gray-900">{order.clinic_name}</span>
                              <span className="text-xs text-gray-400">{date}</span>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">${order.total_value || order.total_points} total</span>
                              {order.points_used > 0 && <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">{order.points_used} pts used</span>}
                              {order.cash_outstanding > 0 && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${order.cash_payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                  {order.cash_payment_status === 'paid' ? `✅ $${order.cash_outstanding?.toFixed(2)} cash paid` : `💰 $${order.cash_outstanding?.toFixed(2)} cash owing`}
                                </span>
                              )}
                            </div>
                            <div className="space-y-0.5 mt-1">
                              {order.items?.map((item, i) => (
                                <p key={i} className="text-sm text-gray-700">
                                  • {item.quantity}× <strong>{item.product}</strong> ({item.variation}) — {item.line_total_points} pts
                                </p>
                              ))}
                            </div>
                            {order.delivery_address && (
                              <p className="text-xs text-gray-500 mt-1">📍 {order.delivery_address}</p>
                            )}
                            {order.notes && (
                              <p className="text-xs text-gray-400 mt-0.5 italic">Note: {order.notes}</p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            {order.cash_outstanding > 0 && order.cash_payment_status !== 'paid' && (
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/product-orders/${order.id}/mark-cash-paid`, { method: 'POST' });
                                    if (res.ok) {
                                      showToast(`✅ $${order.cash_outstanding?.toFixed(2)} cash marked as received`, 'success');
                                      await loadProductOrders();
                                    } else {
                                      const err = await res.json();
                                      showToast(`❌ ${err.detail || 'Failed'}`, 'error');
                                    }
                                  } catch { showToast('❌ Failed to connect', 'error'); }
                                }}
                                className="flex-shrink-0 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-lg">
                                💰 Cash Received
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                setFulfillingOrder(order.id);
                                try {
                                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/product-orders/${order.id}/mark-fulfilled`, { method: 'POST' });
                                  if (res.ok) {
                                    showToast(`✅ Order fulfilled for ${order.clinic_name}`, 'success');
                                    await loadProductOrders();
                                  } else {
                                    const err = await res.json();
                                    showToast(`❌ ${err.detail || 'Failed'}`, 'error');
                                  }
                                } catch { showToast('❌ Failed to connect', 'error'); }
                                finally { setFulfillingOrder(null); }
                              }}
                              disabled={fulfillingOrder === order.id}
                              className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg">
                              {fulfillingOrder === order.id ? '⏳ Saving...' : '📦 Mark Fulfilled'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── PROGRAM SUMMARY ── */}
            {/* Material Requests */}
            {materialRequests.length > 0 && (
              <div className="bg-white rounded-xl border-2 border-teal-300 overflow-hidden" data-testid="material-requests-section">
                <div className="bg-teal-50 px-5 py-3 border-b border-teal-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${materialRequests.filter(r => r.status === 'pending').length > 0 ? 'bg-teal-500 animate-pulse' : 'bg-teal-400'}`}></span>
                    <h3 className="font-semibold text-teal-900">Material Requests</h3>
                  </div>
                  {materialRequests.filter(r => r.status === 'pending').length > 0 && (
                    <span className="text-xs font-bold bg-teal-500 text-white px-2 py-0.5 rounded-full">
                      {materialRequests.filter(r => r.status === 'pending').length} pending
                    </span>
                  )}
                </div>
                <div className="divide-y divide-gray-100">
                  {materialRequests.map(req => {
                    const date = req.requested_date
                      ? new Date(req.requested_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '-';
                    const itemsList = [];
                    if (req.quantity_tags > 0) itemsList.push(`${req.quantity_tags} Pet Tags`);
                    if (req.quantity_brochures > 0) itemsList.push(`${req.quantity_brochures} Brochures`);
                    const isPending = req.status === 'pending';
                    const isCompleted = req.status === 'completed';
                    
                    return (
                      <div key={req.id} className={`px-5 py-4 ${isCompleted ? 'bg-gray-50' : ''}`} data-testid={`material-req-${req.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm text-gray-900">{req.clinic_name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                isPending ? 'bg-amber-100 text-amber-700' :
                                isCompleted ? 'bg-green-100 text-green-700' :
                                req.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{req.status}</span>
                              <span className="text-xs text-gray-500">{date}</span>
                            </div>
                            <div className="text-sm text-gray-700 mt-1">{itemsList.join(' + ')}</div>
                            {req.delivery_address && <div className="text-xs text-gray-500 mt-1">Deliver to: {req.delivery_address}</div>}
                            {req.notes && <div className="text-xs text-gray-400 mt-1 italic">{req.notes}</div>}
                            {isCompleted && req.completed_date && (
                              <div className="text-xs text-green-600 mt-1">Completed: {new Date(req.completed_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                            )}
                          </div>
                          {isPending && (
                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                onClick={async () => {
                                  setCompletingMaterial(req.id);
                                  try {
                                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vet-portal/material-requests/${req.id}/status`, {
                                      method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ status: 'completed' })
                                    });
                                    if (res.ok) {
                                      showToast(`Materials marked as delivered for ${req.clinic_name}`);
                                      setMaterialRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'completed', completed_date: new Date().toISOString() } : r));
                                    } else { showToast('Failed to update', 'error'); }
                                  } catch { showToast('Failed to connect', 'error'); }
                                  finally { setCompletingMaterial(null); }
                                }}
                                disabled={completingMaterial === req.id}
                                className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg"
                                data-testid={`complete-material-${req.id}`}
                              >
                                {completingMaterial === req.id ? 'Saving...' : 'Mark Delivered'}
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vet-portal/material-requests/${req.id}/status`, {
                                      method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ status: 'cancelled' })
                                    });
                                    if (res.ok) {
                                      showToast('Request cancelled');
                                      setMaterialRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'cancelled' } : r));
                                    }
                                  } catch { showToast('Failed', 'error'); }
                                }}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm px-3 py-2 rounded-lg"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          <button
                            onClick={async () => {
                              if (confirmDeleteId !== `mat-${req.id}`) {
                                setConfirmDeleteId(`mat-${req.id}`);
                                setTimeout(() => setConfirmDeleteId(null), 3000);
                                return;
                              }
                              try {
                                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vet-portal/material-requests/${req.id}`, { method: 'DELETE' });
                                if (res.ok) {
                                  showToast(`Deleted material request from ${req.clinic_name}`);
                                  setMaterialRequests(prev => prev.filter(r => r.id !== req.id));
                                  setConfirmDeleteId(null);
                                } else { showToast('Failed to delete', 'error'); }
                              } catch { showToast('Failed to connect', 'error'); }
                            }}
                            className={`text-xs mt-1 px-2 py-1 rounded ${confirmDeleteId === `mat-${req.id}` ? 'bg-red-600 text-white' : 'text-red-400 hover:text-red-600 hover:bg-red-50'}`}
                          >
                            {confirmDeleteId === `mat-${req.id}` ? 'Sure? Click again' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Program Summary */}
            {pendingRedemptions.length > 0 && (
              <div className="bg-white rounded-xl border-2 border-amber-300 overflow-hidden">
                <div className="bg-amber-50 px-5 py-3 border-b border-amber-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                    <h3 className="font-semibold text-amber-900">⏳ Pending Redemptions — Action Required</h3>
                  </div>
                  <span className="text-xs font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">{pendingRedemptions.length}</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {pendingRedemptions.map(r => {
                    const bd = r.bank_details;
                    const date = r.redemption_date ? new Date(r.redemption_date).toLocaleDateString('en-AU', { day:'2-digit', month:'short', year:'numeric' }) : '—';
                    return (
                      <div key={r.id} className="px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap mb-1">
                              <span className="font-bold text-gray-900">{r.clinic_name}</span>
                              <span className="text-lg font-bold text-green-700">${r.cash_value?.toFixed(2)}</span>
                              <span className="text-xs text-gray-400">{date}</span>
                              {r.source === 'vet_portal' && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Via Vet Portal</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{r.points_redeemed} pts · {r.redemption_type}</p>
                            {bd && (
                              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                                <p className="font-semibold text-amber-900 text-xs mb-1">🏦 Bank Transfer Details</p>
                                <div className="grid grid-cols-3 gap-x-4 text-xs">
                                  <span className="text-gray-500">Account Name</span>
                                  <span className="col-span-2 font-medium">{bd.account_name}</span>
                                  <span className="text-gray-500">BSB</span>
                                  <span className="col-span-2 font-mono font-medium">{bd.bsb}</span>
                                  <span className="text-gray-500">Account No.</span>
                                  <span className="col-span-2 font-mono font-medium">{bd.account_number}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            <button
                              onClick={() => markAsPaid(r.id, r.clinic_name, r.cash_value)}
                              disabled={markingPaid === r.id}
                              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                            >
                              {markingPaid === r.id ? 'Saving...' : 'Mark as Paid'}
                            </button>
                            <button
                              onClick={async () => {
                                if (confirmDeleteId !== `red-${r.id}`) {
                                  setConfirmDeleteId(`red-${r.id}`);
                                  setTimeout(() => setConfirmDeleteId(null), 3000);
                                  return;
                                }
                                try {
                                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/redemptions/${r.id}`, { method: 'DELETE' });
                                  if (res.ok) {
                                    showToast(`Deleted redemption for ${r.clinic_name}`);
                                    setPendingRedemptions(prev => prev.filter(x => x.id !== r.id));
                                    setConfirmDeleteId(null);
                                  } else { showToast('Failed to delete', 'error'); }
                                } catch { showToast('Failed to connect', 'error'); }
                              }}
                              className={`text-xs px-4 py-1.5 rounded-lg ${confirmDeleteId === `red-${r.id}` ? 'bg-red-600 text-white' : 'bg-red-100 hover:bg-red-200 text-red-700'}`}
                            >
                              {confirmDeleteId === `red-${r.id}` ? 'Sure? Click again' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">🏥</span>
                  <div>
                    <p className="text-sm font-medium text-teal-600">Active Clinics</p>
                    <p className="text-xl font-bold text-teal-900">{Object.keys(clinicData.clinic_credits || {}).length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">💳</span>
                  <div>
                    <p className="text-sm font-medium text-green-600">Total Points</p>
                    <p className="text-xl font-bold text-green-900">
                      {Object.values(clinicData.clinic_credits || {}).reduce((sum, clinic) => sum + clinic.credits, 0)} pts
                    </p>
                    <p className="text-xs text-green-600">1 pt = $1</p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">💰</span>
                  <div>
                    <p className="text-sm font-medium text-orange-600">Total Value</p>
                    <p className="text-xl font-bold text-orange-900">
                      {formatCurrency(Object.values(clinicData.clinic_credits || {}).reduce((sum, clinic) => sum + clinic.credits, 0))}
                    </p>
                    <p className="text-xs text-orange-600">1 referral = 5 pts = $5</p>
                  </div>
                </div>
              </div>
            </div>

              {/* Clinic Credits Table */}
            {clinicData.clinic_credits && Object.keys(clinicData.clinic_credits).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">🏆 Clinic Point Balances</h3>
                  <p className="text-xs text-gray-500">1 referral = 5 pts · 1 pt = $1</p>
                </div>
                <div className="space-y-3">
                  {Object.entries(clinicData.clinic_credits)
                    .sort(([,a], [,b]) => b.credits - a.credits)
                    .map(([clinicName, data]) => (
                    <div key={clinicName} className="p-3 md:p-4 bg-gray-50 rounded-lg">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm md:text-base truncate">{clinicName}</h4>
                          <p className="text-xs md:text-sm text-gray-500">{data.referrals} referrals from CRM</p>
                        </div>
                        <div className="flex items-center justify-between md:justify-end gap-3">
                          <div className="text-left md:text-right md:mr-4">
                            <p className="font-semibold text-teal-700 text-base md:text-lg">{data.credits} pts</p>
                            <p className="text-xs md:text-sm font-medium text-green-700">{formatCurrency(data.credits)}</p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={async () => {
                                if (auditClinic === clinicName) { setAuditClinic(null); setAuditData(null); return; }
                                setLoadingAudit(true);
                                setAuditClinic(clinicName);
                                try {
                                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clinic-points-audit?clinic_name=${encodeURIComponent(clinicName)}`);
                                  if (res.ok) { const d = await res.json(); setAuditData(d.audit?.[0] || null); }
                                } catch { showToast('Failed to load', 'error'); }
                                setLoadingAudit(false);
                              }}
                              className={`text-xs px-2 py-1.5 rounded font-medium ${auditClinic === clinicName ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                            >
                              {loadingAudit && auditClinic === clinicName ? '...' : 'Review'}
                            </button>
                            <button
                              onClick={async () => {
                                setSelectedClinic({ name: clinicName, data });
                                // Fetch clinic-specific catalogue
                                try {
                                  const catRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/vet-products/for-clinic?clinic_name=${encodeURIComponent(clinicName)}`);
                                  if (catRes.ok) {
                                    const cd = await catRes.json();
                                    const catalogObj = {};
                                    for (const p of (cd.products || [])) {
                                      catalogObj[p.product] = {
                                        variations: p.variations.map(v => ({ name: v.name, price: v.points, overridden: v._overridden }))
                                      };
                                    }
                                    setProductCatalog(catalogObj);
                                  }
                                } catch (e) { console.error("Rewards error:", e); }
                                setShowRedemptionModal(true);
                              }}
                              className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded text-sm font-medium"
                            >
                              Redeem
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Review Panel */}
                      {auditClinic === clinicName && auditData && (
                        <div className="mt-3 p-4 bg-white border border-blue-200 rounded-lg text-sm space-y-3">
                          <div className="font-semibold text-gray-800">{clinicName}</div>
                          <div className="grid grid-cols-3 gap-2 md:gap-3 text-center">
                            <div className="bg-blue-50 rounded-lg p-3">
                              <div className="text-2xl font-bold text-blue-800">{auditData.referrals}</div>
                              <div className="text-xs text-blue-600">Pets in CRM</div>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                              <div className="text-2xl font-bold text-green-800">{auditData.pts_from_referrals}</div>
                              <div className="text-xs text-green-600">Points Earned</div>
                            </div>
                            <div className="bg-teal-50 rounded-lg p-3">
                              <div className="text-2xl font-bold text-teal-800">{auditData.pts_from_referrals - auditData.total_spent}</div>
                              <div className="text-xs text-teal-600">Clean Balance</div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 pt-2">
                            <button
                              onClick={async () => {
                                if (confirmDeleteId !== `reset-${clinicName}`) {
                                  setConfirmDeleteId(`reset-${clinicName}`);
                                  setTimeout(() => setConfirmDeleteId(null), 3000);
                                  return;
                                }
                                try {
                                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clinic-reset/${encodeURIComponent(clinicName)}`, { method: 'POST' });
                                  if (res.ok) {
                                    const d = await res.json();
                                    showToast(`Reset ${clinicName}: ${d.pets_in_crm} referrals = ${d.clean_balance} pts`);
                                    setConfirmDeleteId(null);
                                    window.location.reload();
                                  } else { showToast('Failed', 'error'); }
                                } catch { showToast('Failed', 'error'); }
                              }}
                              className={`text-xs px-3 py-2 rounded-lg font-medium ${confirmDeleteId === `reset-${clinicName}` ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                            >
                              {confirmDeleteId === `reset-${clinicName}` ? 'Sure? Click again' : 'Reset Clinic'}
                            </button>
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="number"
                                min="1"
                                placeholder="# referrals"
                                className="w-24 text-xs px-2 py-2 border rounded-lg"
                                id={`add-ref-${clinicName}`}
                              />
                              <button
                                onClick={async () => {
                                  const input = document.getElementById(`add-ref-${clinicName}`);
                                  const num = parseInt(input?.value);
                                  if (!num || num <= 0) { showToast('Enter a number', 'error'); return; }
                                  try {
                                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/adjust-points`, {
                                      method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ clinic_name: clinicName, adjustment_amount: num * 5, reason: `Additional referrals (${num})` })
                                    });
                                    if (res.ok) {
                                      showToast(`Added ${num} referrals (${num * 5} pts) for ${clinicName}`);
                                      window.location.reload();
                                    } else { showToast('Failed', 'error'); }
                                  } catch { showToast('Failed', 'error'); }
                                }}
                                className="text-xs px-3 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                              >
                                Add Referrals
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        </div>
        {/* End Scrollable Content */}

        {/* Redemption Modal */}
        {showRedemptionModal && selectedClinic && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">

              {/* Modal Header */}
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="text-white font-bold text-lg">💳 Redeem Points</h3>
                  <p className="text-teal-200 text-xs mt-0.5">{selectedClinic.name} · {selectedClinic.data.credits} pts available (${selectedClinic.data.credits})</p>
                </div>
                <button onClick={() => { setShowRedemptionModal(false); setSelectedClinic(null); setRedemptionType(''); setSelectedProducts([]); setCashAmount(0); }}
                  className="text-white text-2xl font-bold hover:text-teal-200">×</button>
              </div>

              <div className="overflow-y-auto flex-1 p-6">

                {/* Type selector */}
                {!redemptionType && (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: 'cash',     icon: '💵', label: 'Cash',     sub: '$1 per point',   color: 'green'  },
                      { id: 'products', icon: '🎁', label: 'Products', sub: 'Vet supplies',    color: 'blue'   },
                      { id: 'adjust',   icon: '🔧', label: 'Adjust',   sub: 'Manual points',  color: 'orange' },
                    ].map(t => (
                      <button key={t.id} onClick={() => setRedemptionType(t.id)}
                        className={`p-4 border-2 border-${t.color}-200 bg-${t.color}-50 hover:bg-${t.color}-100 rounded-xl text-center transition-colors`}>
                        <div className="text-2xl mb-1">{t.icon}</div>
                        <p className={`font-semibold text-${t.color}-900 text-sm`}>{t.label}</p>
                        <p className={`text-xs text-${t.color}-600 mt-0.5`}>{t.sub}</p>
                      </button>
                    ))}
                  </div>
                )}

                {/* ── CASH ── */}
                {redemptionType === 'cash' && (
                  <div className="space-y-4">
                    <button onClick={() => setRedemptionType('')} className="text-sm text-gray-500 hover:text-gray-700">← Back</button>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount to Redeem ($)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500 font-medium">$</span>
                        <input type="number" min="1" max={selectedClinic.data.credits}
                          value={cashAmount}
                          onChange={e => setCashAmount(Math.min(parseInt(e.target.value) || 0, selectedClinic.data.credits))}
                          className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 focus:ring-2 focus:ring-teal-400"
                          placeholder="Enter dollar amount" />
                      </div>
                      <p className="text-sm text-teal-700 mt-2 font-medium">
                        = {cashAmount || 0} pts deducted · Remaining: {formatCurrency(selectedClinic.data.credits - (cashAmount || 0))}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        if (!cashAmount || cashAmount <= 0) return;
                        try {
                          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/redeem-cash`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ clinic_name: selectedClinic.name, points_to_redeem: cashAmount, redemption_type: 'cash' })
                          });
                          if (res.ok) {
                            showToast(`✅ Redeemed ${formatCurrency(cashAmount)} for ${selectedClinic.name}`, 'success');
                            window.location.reload();
                          } else {
                            const err = await res.json();
                            showToast(`❌ ${err.detail || 'Failed'}`, 'error');
                          }
                        } catch { showToast('❌ Failed to process', 'error'); }
                      }}
                      disabled={!cashAmount || cashAmount <= 0}
                      className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white py-3 rounded-xl font-semibold">
                      Process Cash Redemption
                    </button>
                  </div>
                )}

                {/* ── PRODUCTS ── */}
                {redemptionType === 'products' && (() => {
                  const balance = selectedClinic.data.credits;
                  const totalPts = selectedProducts.reduce((s, i) => s + i.points * i.qty, 0);

                  const updateQty = (product, variation, points, delta) => {
                    setSelectedProducts(prev => {
                      const key = `${product}||${variation}`;
                      const existing = prev.find(i => i.key === key);
                      if (!existing) {
                        return delta > 0 ? [...prev, { key, product, variation, points, qty: 1 }] : prev;
                      }
                      const newQty = existing.qty + delta;
                      if (newQty <= 0) return prev.filter(i => i.key !== key);
                      return prev.map(i => i.key === key ? { ...i, qty: newQty } : i);
                    });
                  };

                  return (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <button onClick={() => { setRedemptionType(''); setSelectedProducts([]); }} className="text-sm text-gray-500 hover:text-gray-700">← Back</button>
                        <span className="text-xs text-gray-500">{balance} pts available</span>
                      </div>

                      {/* Product cards */}
                      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                        {Object.entries(productCatalog).map(([productName, info]) => (
                          <div key={productName} className="border border-gray-200 rounded-xl overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                              <p className="font-semibold text-gray-900 text-sm">🎁 {productName}</p>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {info.variations.map(v => {
                                const key = `${productName}||${v.name}`;
                                const item = selectedProducts.find(i => i.key === key);
                                const qty = item?.qty || 0;
                                return (
                                  <div key={v.name} className="flex items-center justify-between px-4 py-2.5">
                                    <div>
                                      <span className="text-sm text-gray-800">{v.name}</span>
                                      <span className="ml-2 text-xs font-semibold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded-full">{v.price} pts</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {qty > 0 && (
                                        <>
                                          <button onClick={() => updateQty(productName, v.name, v.price, -1)}
                                            className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 flex items-center justify-center font-bold text-lg leading-none">−</button>
                                          <span className="w-5 text-center text-sm font-semibold">{qty}</span>
                                        </>
                                      )}
                                      <button onClick={() => updateQty(productName, v.name, v.price, 1)}
                                        disabled={totalPts + v.price > balance && qty === 0}
                                        className="w-7 h-7 rounded-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-200 text-white flex items-center justify-center font-bold text-lg leading-none">+</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Order summary */}
                      {selectedProducts.length > 0 && (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                          <p className="text-sm font-semibold text-teal-900 mb-2">Order Summary</p>
                          {selectedProducts.map(i => (
                            <div key={i.key} className="flex justify-between text-sm text-teal-800 mb-0.5">
                              <span>{i.qty}× {i.product} ({i.variation})</span>
                              <span className="font-medium">{i.points * i.qty} pts</span>
                            </div>
                          ))}
                          <div className="border-t border-teal-300 mt-2 pt-2 flex justify-between font-bold text-teal-900">
                            <span>Total</span>
                            <span>{totalPts} pts (${totalPts})</span>
                          </div>
                          <p className="text-xs text-teal-600 mt-1">Remaining after: {balance - totalPts} pts</p>
                        </div>
                      )}

                      <button
                        disabled={selectedProducts.length === 0}
                        onClick={async () => {
                          try {
                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/redeem-products-crm`, {
                              method: 'POST', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                clinic_name: selectedClinic.name,
                                items: selectedProducts.map(i => ({ product: i.product, variation: i.variation, quantity: i.qty })),
                                points_to_use: Math.min(totalPts, balance)
                              })
                            });
                            if (res.ok) {
                              const d = await res.json();
                              const cashMsg = d.cash_outstanding > 0 ? ` Invoice $${d.cash_outstanding.toFixed(2)} cash to clinic.` : '';
                              showToast(`✅ Product order placed for ${selectedClinic.name}.${cashMsg}`, 'success');
                              setShowRedemptionModal(false); setSelectedClinic(null); setRedemptionType(''); setSelectedProducts([]);
                              await loadProductOrders();
                            } else {
                              const err = await res.json();
                              showToast(`❌ ${err.detail || 'Failed'}`, 'error');
                            }
                          } catch { showToast('❌ Failed to place order', 'error'); }
                        }}
                        className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white py-3 rounded-xl font-semibold">
                        {selectedProducts.length === 0 ? 'Select items above' : `Place Order — ${Math.min(totalPts, balance)} pts${totalPts > balance ? ` + $${(totalPts - balance).toFixed(2)} cash` : ''}`}
                      </button>
                    </div>
                  );
                })()}

                {/* ── ADJUST ── */}
                {redemptionType === 'adjust' && (
                  <div className="space-y-4">
                    <button onClick={() => setRedemptionType('')} className="text-sm text-gray-500 hover:text-gray-700">← Back</button>
                    <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                      <label className="block text-sm font-medium text-orange-800 mb-2">Adjustment Amount (negative to subtract)</label>
                      <input type="number" value={cashAmount} onChange={e => setCashAmount(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                        placeholder="e.g. +5 or -3" />
                      <p className="text-sm text-orange-700 mt-2">New balance: {selectedClinic.data.credits + (cashAmount || 0)} pts = {formatCurrency(selectedClinic.data.credits + (cashAmount || 0))}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                      <input type="text" value={adjustmentReason || ''} onChange={e => setAdjustmentReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                        placeholder="Enter reason for adjustment" />
                    </div>
                    <button
                      disabled={!cashAmount || cashAmount === 0 || !adjustmentReason?.trim()}
                      onClick={async () => {
                        try {
                          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic-rewards/adjust-points`, {
                            method: 'PUT', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ clinic_name: selectedClinic.name, adjustment_amount: cashAmount, reason: adjustmentReason })
                          });
                          if (res.ok) {
                            showToast(`✅ Points adjusted for ${selectedClinic.name}`, 'success');
                            window.location.reload();
                          } else {
                            const err = await res.json();
                            showToast(`❌ ${err.detail || 'Failed'}`, 'error');
                          }
                        } catch { showToast('❌ Failed to adjust', 'error'); }
                      }}
                      className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white py-3 rounded-xl font-semibold">
                      Apply Adjustment
                    </button>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};


export default ClinicRewardsView;
